// A drop, a folder, some files or a zip → one flat list of audio files.
//
// Everything a producer can hand over is normalized here, in the tab: zips
// are unpacked in the browser and nested folders flattened (the relative path
// stays, as a naming hint). The server only ever sees single audio files, and
// a file the producer leaves out never leaves the machine.
//
// What is skipped is skipped silently and counted: macOS resource forks
// (`__MACOSX/`, `._x.wav`), `.DS_Store`, hidden files, and anything that isn't
// audio — a stems folder routinely carries a project file, a readme, artwork.
// A folder is judged by its paths BEFORE any file is read: a custom-icon
// file ("Icon\r"), an alias, an iCloud placeholder or an unreadable
// subfolder would otherwise fail the whole drop at `entry.file()`. Anything
// that cannot be read is counted as skipped, and only a drop with nothing
// readable at all is an error.

import { unzip, type Unzipped } from "fflate";

import { AUDIO_EXTENSIONS, WEB_UPLOAD_LIMITS, type AudioExtension } from "./config";

export interface IntakeFile {
  /** Path inside what was dropped, folders included: `Stems/Drums/Kick.wav`. */
  path: string;
  /** File name without its extension: `Kick`. */
  name: string;
  ext: AudioExtension;
  size: number;
  blob: Blob;
}

export interface Intake {
  files: IntakeFile[];
  /** Hidden, system and non-audio files left out. */
  skipped: number;
  /** The dropped folder's or zip's name, when there was exactly one — a title hint. */
  rootName: string | null;
}

/** A file with the path it was found at. */
export interface FoundFile {
  path: string;
  file: Blob;
}

// ---------------------------------------------------------------------------
// Pure: what to keep
// ---------------------------------------------------------------------------

export type PathVerdict = { keep: true; ext: AudioExtension } | { keep: false; zip: boolean };

/** Whether a path is audio worth keeping, a zip to open, or noise. */
export function judgePath(path: string): PathVerdict {
  const parts = path.split("/").filter((p) => p.length > 0);
  const base = parts.at(-1) ?? "";
  // Any hidden or macOS-metadata component hides everything under it; so
  // does a name with a control character (the Finder's "Icon\r").
  if (parts.some((p) => p.startsWith(".") || p === "__MACOSX" || /[\u0000-\u001f]/.test(p))) return { keep: false, zip: false };
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return { keep: false, zip: false };
  const ext = base.slice(dot + 1).toLowerCase();
  if (ext === "zip") return { keep: false, zip: true };
  if ((AUDIO_EXTENSIONS as readonly string[]).includes(ext)) return { keep: true, ext: ext as AudioExtension };
  return { keep: false, zip: false };
}

/** `Stems/Drums/Kick.wav` → `Kick`. */
export function baseName(path: string): string {
  const base = path.split("/").filter((p) => p.length > 0).at(-1) ?? path;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
}

/** The single top-level folder (or zip) everything came from, if there is one. */
export function commonRoot(paths: string[]): string | null {
  let root: string | null = null;
  for (const p of paths) {
    const parts = p.split("/").filter((x) => x.length > 0);
    if (parts.length < 2) return null;
    if (root === null) root = parts[0]!;
    else if (root !== parts[0]) return null;
  }
  return root;
}

/** Sort and pick: audio kept (natural order by path), everything else counted. */
export function sortIntake(found: FoundFile[]): { files: IntakeFile[]; skipped: number } {
  const files: IntakeFile[] = [];
  let skipped = 0;
  for (const f of found) {
    const v = judgePath(f.path);
    if (!v.keep) {
      skipped++;
      continue;
    }
    files.push({ path: f.path, name: baseName(f.path), ext: v.ext, size: f.file.size, blob: f.file });
  }
  files.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true, sensitivity: "base" }));
  return { files, skipped };
}

// ---------------------------------------------------------------------------
// The browser: picking the files out of whatever was handed over
// ---------------------------------------------------------------------------

/** Thrown with a message the producer can read. */
export class IntakeError extends Error {}

/** From `<input type="file" multiple>` or `<input webkitdirectory>`. */
export async function intakeFromFileList(list: FileList | File[]): Promise<Intake> {
  const found = [...list].map((file) => ({ path: file.webkitRelativePath || file.name, file }));
  return finish(found);
}

/** From a drop: folders are walked, zips opened. */
export async function intakeFromDataTransfer(dt: DataTransfer): Promise<Intake> {
  const entries = [...dt.items]
    .filter((i) => i.kind === "file")
    .map((i) => i.webkitGetAsEntry?.() ?? null);
  // A browser without the entries API hands over plain files (no folders).
  if (entries.some((e) => e === null)) return intakeFromFileList([...dt.files]);
  const found: FoundFile[] = [];
  const unread = { n: 0 };
  for (const entry of entries) await walk(entry!, "", found, unread);
  return finish(found, unread.n);
}

async function finish(found: FoundFile[], unreadable = 0): Promise<Intake> {
  const expanded: FoundFile[] = [];
  let skippedInZips = 0;
  for (const f of found) {
    const v = judgePath(f.path);
    if (!v.keep && v.zip) {
      try {
        const z = await openZip(f);
        expanded.push(...z.files);
        skippedInZips += z.skipped;
      } catch (err) {
        // A zip that will not open, among other files, is one more thing
        // skipped; dropped on its own, it is the problem.
        if (found.length === 1) throw err;
        skippedInZips++;
      }
    } else {
      expanded.push(f);
    }
  }
  const { files, skipped } = sortIntake(expanded);
  const rootName = commonRoot(expanded.map((f) => f.path));
  return { files, skipped: skipped + skippedInZips + unreadable, rootName: rootName === null ? null : baseName(rootName) };
}

/** Whether a path is worth reading at all: audio, or a zip to look inside. */
export function worthReading(path: string): boolean {
  const v = judgePath(path);
  return v.keep || v.zip;
}

async function walk(entry: FileSystemEntry, prefix: string, out: FoundFile[], unread: { n: number }): Promise<void> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    // Judge by the name first: a file that is not audio is never read, so a
    // custom-icon file or an alias that will not open cannot fail the drop.
    if (!worthReading(path)) {
      unread.n++;
      return;
    }
    try {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      out.push({ path, file });
    } catch {
      unread.n++;
    }
    return;
  }
  if (!entry.isDirectory) return;
  // Hidden folders are skipped whole rather than walked and then discarded.
  if (entry.name.startsWith(".") || entry.name === "__MACOSX") return;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries answers in batches (100 in Chrome) until it answers empty. A
  // folder that will not list (permissions, a stale alias) is skipped whole.
  for (;;) {
    let batch: FileSystemEntry[];
    try {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    } catch {
      unread.n++;
      return;
    }
    if (batch.length === 0) break;
    for (const child of batch) await walk(child, path, out, unread);
  }
}

/** A zip's audio, as files under `<zip name>/…`. Only audio is decompressed. */
async function openZip(zip: FoundFile): Promise<{ files: FoundFile[]; skipped: number }> {
  // Read whole: fflate wants the bytes. A zip bigger than the upload could
  // ever be is refused before reading it into memory.
  const name = baseName(zip.path);
  if (zip.file.size > WEB_UPLOAD_LIMITS.maxUploadBytes * 2) {
    throw new IntakeError(`${name}.zip is too big to open here.`);
  }
  const bytes = new Uint8Array(await zip.file.arrayBuffer());
  let skipped = 0;
  const entries = await new Promise<Unzipped>((resolve, reject) =>
    unzip(
      bytes,
      {
        filter: (f) => {
          if (f.name.endsWith("/")) return false; // a folder, not a file
          const keep = judgePath(f.name).keep;
          if (!keep) skipped++;
          return keep;
        },
      },
      (err, out) => (err ? reject(err) : resolve(out)),
    ),
  ).catch(() => {
    throw new IntakeError(`${name}.zip couldn't be opened. Is it a normal zip file?`);
  });
  const files = Object.entries(entries).map(([path, data]) => ({
    path: `${name}/${path}`,
    file: new Blob([data as Uint8Array<ArrayBuffer>]),
  }));
  return { files, skipped };
}
